import React, { useEffect, useState } from "react";
import DrawerCustom from "../../../../shared/general/Drawer";
import { Autocomplete, Box, Checkbox, FormControlLabel, Grid, InputAdornment, MenuItem, styled, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import { useTranslation } from "react-i18next";
import DatePickerCustom from "../../../../shared/date/datePicker";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import dayjs from "dayjs";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import vendorService from "../../../../../../services/vendor.service";
import { getEmployeePersonnelLevelChange, getEmployeeProfile } from "../../../../../../actions/employee";
import { createEmployeePersonnelLevel, getEmployeePersonnelLevelById, updateEmployeePersonnelLevel } from "../../../../../../actions/level";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
  "& .field-label": {
    paddingBottom: "4px",
    fontWeight: "500",
    fontSize: "14px",
  },
})

const DrawerEditEmployeePersonnelLevel = (props) => {

  const { drawerConfig, handleClose } = props;

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { result: employeeProfile } = useSelector(state => state.employeeProfile);
  const [personnelLevelList, setPersonnelLevelList] = useState([]);

  const validateYupSchema = yup.object({
    level: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
    start: yup.mixed().nullable().required(`${t("ThisFieldIsRequired")}`),
  });

  const useHookForm = useForm({
    defaultValues: {
      level: null,
      start: null,
      remark: "",
      isCurrent: false,
    },
    resolver: yupResolver(validateYupSchema),
    mode: "all",
  });

  const onSubmit = async (data) => {

    const formData = {
      idLevel: data.level.idLevel,
      start: dayjs(data.start).format("YYYY-MM-DD"),
      remark: data.remark? data.remark: null,
    }

    let result = null;

    if(drawerConfig.isEdit){
      result = await dispatch(updateEmployeePersonnelLevel(drawerConfig.data.idEmployeePersonnelLevel, formData))
    } else {
      formData.idEmp = employeeProfile.idEmp;
      result = await dispatch(createEmployeePersonnelLevel(formData))
    }

    if(result && result.status === 200){
      if(!result.data.success){
        dispatch(openNotificationAlert({
          type: "error",
          message: result.data.message,
        }));
      } else {
        dispatch(openNotificationAlert({
          type: "success",
          message: t("DataSaveSuccessful"),
        }));
        dispatch(getEmployeeProfile(employeeProfile.idEmp));
        dispatch(getEmployeePersonnelLevelChange(employeeProfile.idEmp));
        handleClose();
      }
    } else if (result && result.data.message) {
      dispatch(openNotificationAlert({
        type: "error",
        message: result.data.message,
      }));
    } else {
      dispatch(openNotificationAlert({
        type: "error",
        message: t("AnErrorOccurred"),
      }));
    }
  }

  useEffect(() => {
    if(drawerConfig.isOpen){
      const fetchMasterData = vendorService.getLevelByVendorId(employeeProfile.idVendor).then(res => {
        setPersonnelLevelList(res.data);
        return res;
      })

      if(drawerConfig.isEdit){

        const fetchDataDetail = dispatch(getEmployeePersonnelLevelById(drawerConfig.data.idEmployeePersonnelLevel));

        Promise.all([fetchMasterData, fetchDataDetail]).then(([resMaster, resDetail]) => {

          const masterList = resMaster.data;
          const detail = resDetail.data;

          const selectedLevel = masterList.find(item => item.idLevel === detail.idLevel);

          useHookForm.reset({
            level: selectedLevel || null,
            start: detail.start? dayjs(detail.start): null,
            remark: detail.remark || "",
            isCurrent: true,
          });
        });

      } else {
        useHookForm.reset({
          level: null,
          start: null,
          remark: "",
          isCurrent: true,
        })
      }
    }
  }, [drawerConfig.isOpen]);

  return (
    <DrawerCustom
      title={t("EditEmployeePersonnelLevel")}
      anchor="right"
      open={drawerConfig.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      <StyledRoot>
        <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight="500" gutterBottom>{t("EmployeePersonnelLevel")}</Typography>
              <Controller
                name="level"
                control={useHookForm.control}
                render={({field, fieldState}) => (
                  <Autocomplete
                    {...field}
                    options={[...personnelLevelList].sort((a, b) => {
                      const cmp = a.companyName.localeCompare(b.companyName);
                      if (cmp !== 0) return cmp;
                      return a.groupLevelName.localeCompare(b.groupLevelName);
                    })}
                    groupBy={(option) => `${option.companyName} - ${option.groupLevelName}`}
                    getOptionLabel={(option) => `(${option.groupLevelName}) ${option.levelName}`}
                    isOptionEqualToValue={(option, value) => option.idLevel === value.idLevel}
                    renderOption={(props, option) => (
                      <MenuItem {...props} key={option.idLevel} sx={{ borderBottom: "1px solid #f0f0f0" }}>
                        <Box>
                          <Typography fontSize="14px" fontWeight="500">
                            {option.levelName}
                          </Typography>
                          <Typography fontSize="11px" color="text.secondary">
                            ID: {option.idLevel}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                    
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        placeholder={t("PleaseSelectInformation")}
                        onBlur={field.onBlur}
                        error={fieldState.error? true: false}
                        helperText={fieldState.error? fieldState.error.message: null}
                      />
                    )}
                    value={field.value}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    noOptionsText={t("NoData")}
                    disableClearable
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight="500" gutterBottom>{t("StartDate")}</Typography>
              <Controller
                name="start"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <DatePickerCustom
                    {...field}
                    views={["year", "month", "day"]}
                    inputFormat="DD MMMM YYYY"
                    openTo="day"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    disableMaskedInput
                    renderInput={(params) => (
                      <TextFieldTheme
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: t("SelectDate"),
                          readOnly: true
                        }}
                        onBlur={field.onBlur}
                        error={fieldState.error? true : false}
                        helperText={fieldState.error? fieldState.error.message : null}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container justifyContent="space-between" alignItems="center" paddingBottom="8px">
                <Typography fontSize="14px" fontWeight="500">{t("EndDate")}</Typography>
                <Controller
                  name={"isCurrent"}
                  control={useHookForm.control}
                  render={({field}) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                          }}
                          disabled={true}
                        />
                      }
                      label={<Typography fontSize="14px" fontWeight="500">{t("Current")}</Typography>}
                      labelPlacement="start"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography fontSize="14px" fontWeight="500" gutterBottom>{t("Description")}</Typography>
              <Controller
                name="remark"
                control={useHookForm.control}
                render={({ field, fieldState }) => (
                  <TextFieldTheme
                    {...field}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          style={{
                            alignSelf: "flex-end",
                            fontSize: "12px",
                            userSelect: "none",
                          }}
                        >
                          {500 - field.value.length}
                        </InputAdornment>
                      ),
                    }}
                    multiline
                    minRows={3}
                    placeholder={`${t("Description")}...`}
                    helperText={
                      fieldState.error? fieldState.error.message: null
                    }
                    error={
                      fieldState.error? true : false
                    }
                    onChange={(event) => {
                      if (event.target.value.length > 500) {
                        field.onChange(event.target.value.slice(0, 500));
                      } else {
                        field.onChange(event.target.value);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} container justifyContent="space-between">
              <ButtonBlue variant="text" onClick={handleClose} disabled={useHookForm.formState.isSubmitting}>{t("Cancel")}</ButtonBlue>
              <ButtonBlue variant="contained" type="submit" disabled={useHookForm.formState.isSubmitting}>{t("Save")}</ButtonBlue>
            </Grid>
          </Grid>
        </form>
      </StyledRoot>
    </DrawerCustom>
  )
}

export default DrawerEditEmployeePersonnelLevel;